/**
 * SearchLiveMonitor 组件
 *
 * 实时监控标签页，每5秒自动刷新搜索供应商状态。
 */

import React, { useState, useEffect, useCallback } from 'react';
import {
  searchAdminApi,
  type SearchLiveData,
  type SearchLiveProvider,
  type SearchLiveKey,
} from '../../../services/searchAdminApi';
import { StatusBadge } from '../shared';

export const SearchLiveMonitor: React.FC = () => {
  const [data, setData] = useState<SearchLiveData | null>(null);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState('');
  const [isPaused, setIsPaused] = useState(false);

  const fetchData = useCallback(async () => {
    try {
      const result = await searchAdminApi.getLiveMonitoring();
      setData(result);
      setError('');
    } catch (err) {
      setError(err instanceof Error ? err.message : '获取监控数据失败');
    } finally {
      setLoading(false);
    }
  }, []);

  useEffect(() => {
    if (isPaused) return;
    fetchData();
    const interval = setInterval(fetchData, 5000);
    return () => clearInterval(interval);
  }, [fetchData, isPaused]);

  const formatTime = (ts: string) => {
    try {
      return new Date(ts).toLocaleTimeString();
    } catch {
      return ts;
    }
  };

  if (loading) {
    return (
      <div className="text-center py-8 text-gray-500">加载监控数据...</div>
    );
  }

  return (
    <div className="space-y-4">
      <div className="flex items-center justify-between">
        <div className="flex items-center gap-3">
          <h3 className="text-sm font-medium text-gray-600">实时监控</h3>
          {data?.timestamp && (
            <span className="text-xs text-gray-400">
              更新于 {formatTime(data.timestamp)}
            </span>
          )}
        </div>
        <button
          onClick={() => setIsPaused(!isPaused)}
          className={`px-3 py-1 rounded text-sm ${
            isPaused
              ? 'bg-green-100 text-green-600'
              : 'bg-gray-100 text-gray-600'
          }`}
        >
          {isPaused ? '继续' : '暂停'}
        </button>
      </div>

      {error && (
        <div className="p-3 bg-red-50 border border-red-200 rounded text-red-600 text-sm">
          {error}
        </div>
      )}

      {data?.summary && (
        <div className="grid grid-cols-2 md:grid-cols-4 gap-4">
          <div className="bg-white rounded-lg border border-gray-200 p-4">
            <div className="text-sm text-gray-500">活跃并发</div>
            <div className="text-lg font-bold mt-1">
              {data.summary.total_concurrency}
            </div>
          </div>
          <div className="bg-white rounded-lg border border-gray-200 p-4">
            <div className="text-sm text-gray-500">当前 RPM</div>
            <div className="text-lg font-bold mt-1">
              {data.summary.total_rpm}
            </div>
          </div>
          <div className="bg-white rounded-lg border border-gray-200 p-4">
            <div className="text-sm text-gray-500">健康密钥</div>
            <div className="text-lg font-bold mt-1 text-green-600">
              {data.summary.healthy_keys}
            </div>
          </div>
          <div className="bg-white rounded-lg border border-gray-200 p-4">
            <div className="text-sm text-gray-500">异常密钥</div>
            <div className="text-lg font-bold mt-1 text-red-600">
              {data.summary.error_keys}
            </div>
          </div>
        </div>
      )}

      {data?.providers && data.providers.length > 0 ? (
        <div className="space-y-3">
          {data.providers.map((provider: SearchLiveProvider) => (
            <div
              key={provider.provider_name}
              className="bg-white rounded-lg border border-gray-200"
            >
              <div className="p-4 border-b border-gray-200 flex items-center justify-between">
                <div className="flex items-center gap-2">
                  <h4 className="font-medium text-gray-800">
                    {provider.display_name || provider.provider_name}
                  </h4>
                  <StatusBadge
                    variant={provider.enabled ? 'success' : 'neutral'}
                    size="sm"
                  >
                    {provider.enabled ? '启用' : '禁用'}
                  </StatusBadge>
                </div>
              </div>
              <div className="divide-y divide-gray-100">
                {provider.keys.map((key: SearchLiveKey) => (
                  <div
                    key={key.key_index}
                    className="p-3 flex items-center justify-between"
                  >
                    <div className="flex items-center gap-3">
                      <span className="text-sm text-gray-500">
                        #{key.key_index}
                      </span>
                      <StatusBadge
                        variant={key.available ? 'success' : 'error'}
                        size="sm"
                        dot
                        pulse={!key.available}
                      >
                        {key.available ? '可用' : '不可用'}
                      </StatusBadge>
                      {key.circuit_state === 'open' && (
                        <StatusBadge variant="error" size="sm">
                          熔断
                        </StatusBadge>
                      )}
                      {key.circuit_state === 'half_open' && (
                        <StatusBadge variant="warning" size="sm">
                          半开
                        </StatusBadge>
                      )}
                    </div>
                    <div className="flex items-center gap-4 text-sm">
                      <span className="text-gray-500">
                        并发: {key.concurrency}
                      </span>
                      <span className="text-gray-500">RPM: {key.rpm}</span>
                      <span
                        className={
                          key.avg_latency_ms > 3000
                            ? 'text-red-600'
                            : key.avg_latency_ms > 1000
                              ? 'text-yellow-600'
                              : 'text-green-600'
                        }
                      >
                        {key.avg_latency_ms > 0
                          ? `${Math.round(key.avg_latency_ms)}ms`
                          : '-'}
                      </span>
                      {key.last_error && (
                        <span
                          className="text-xs text-red-500 truncate max-w-48"
                          title={key.last_error}
                        >
                          {key.last_error}
                        </span>
                      )}
                    </div>
                  </div>
                ))}
              </div>
            </div>
          ))}
        </div>
      ) : (
        <div className="text-center py-8 text-gray-500">
          暂无监控数据
        </div>
      )}

      <div className="p-3 bg-blue-50 rounded-lg text-sm text-blue-700">
        <strong>实时监控说明：</strong>自动每5秒刷新供应商状态。
        <div className="text-xs text-blue-600 mt-1">
          刷新间隔：5秒 | 显示所有启用的供应商和密钥状态
        </div>
      </div>
    </div>
  );
};

export default SearchLiveMonitor;
